import { Component, computed, inject, signal } from '@angular/core';
import { Button } from '@ntv360/component-pantry';

import { ExportService } from '@core';
import { UILicense } from '@features/licenses/interfaces';
import { TableComponent, TableToolbarComponent } from '@shared';
import { SVG_ICONS } from '@shared/constants';
import { TableToolbarButton } from '@shared/interfaces';
import { CONTENTS_COLUMNS, CONTENTS_DATA } from '../../../../constants';

@Component({
    selector: 'app-contents',
    standalone: true,
    imports: [TableToolbarComponent, TableComponent, Button],
    templateUrl: './contents.component.html',
    styleUrl: './contents.component.scss',
})
export class ContentsComponent {
    /**
     * Injected export service for CSV export.
     */
    private exportService = inject(ExportService);
    /**
     * Column definitions for the license table.
     */
    public contentColumns = CONTENTS_COLUMNS;

    /**
     * Data for the license table.
     */
    public contentData = CONTENTS_DATA;

    /**
     * Reference to the collection of SVG icon names used in the component.
     */
    public selectedRows = signal<UILicense[]>([]);

    /**
     * Reference to navbar icon constants.
     */
    public icons = SVG_ICONS;

    /**
     * Path to the SVG sprite file.
     */
    public spritePath = 'assets/icons/icons-sprite.svg';

    /**
     * Computed list of toolbar buttons, reacting to selection changes.
     */
    public toolbarButtons = computed<TableToolbarButton[]>(() => {
        const selectionCount = this.selectedRows().length;
        return [
            {
                buttonName: 'Bulk Delete',
                icon: 'delete',
                variant: selectionCount > 2 ? 'danger' : 'secondary',
            },
            {
                buttonName: 'Export',
                icon: 'export',
                variant: 'accent',
            },
        ];
    });

    /**
     * Handles action click events from the table.
     *Triggers when the user clicks on an action button in the table toolbar.
     *
     * @param event - The action event containing action name and row data
     */
    public onActionClick(event: { action: string; row: any }): void {
        console.log('Action click:', event);
    }

    /**
       * Handles cell click events from the table (for columns with hasRedirect: true).
       *Triggers when the user clicks on a cell that has a redirect link.
  
       * @param event - The cell event containing field name, value, and row data
     */
    public onCellClick(event: { field: string; value: any; row: any }): void {
        // Open single license page in a new window
        if (event.row.id) {
            const url = `/licenses/${event.row.id}`;
            window.open(url, '_blank');
        }
    }

    /**
     * Handles selection change events from the table.
     *Triggers when the user selects or deselects a row.
     *
     * @param selectedRows - The array of currently selected rows
     */
    public onSelectionChange(selectedRows: UILicense[]): void {
        this.selectedRows.set(selectedRows);
    }

    /**
     * Handles toolbar action click events.
     * This returns the action name, Example Clicked the Delete Button will return 'Bulk Delete'
     *
     * @param action - The action name
     */
    public onToolbarAction(action: string): void {
        if (action === 'Export') {
            const dataToExport =
                this.selectedRows().length > 0 ? this.selectedRows() : this.contentData;

            this.exportService.exportToExcel(dataToExport, 'Contents', this.contentColumns);
        }
    }
}
